'use client'

import { useState, useContext, useEffect, useCallback } from 'react'
import { Button } from '@/components/ui/shadcn/button'
import { Checkbox } from '@/components/ui/shadcn/checkbox'
import { Alert, AlertDescription } from '@/components/ui/shadcn/alert'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/shadcn/table'
import { CheckCircle2, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { AuthContext } from '@/contexts/AuthContext'
import { fetcher } from '@/lib/api/fetch'
import { SEVERITIES, CHANNELS } from '@/lib/notifications'

export default function NotificationSettingsCard({
  authContext: authContextProp,
  org: orgProp,
} = {}) {
  const authContextValue = useContext(AuthContext)
  const authContext = authContextProp || authContextValue
  const org = orgProp || authContext?.user?.organization
  const orgId = org?.id

  const [map, setMap] = useState({}) // { severity: [channels] }
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState(null)

  const load = useCallback(async () => {
    if (!orgId) return
    setLoading(true)
    setError(null)
    try {
      const res = await fetcher(
        `/api/admin/organization/${orgId}/settings/notifications`,
        { method: 'GET' },
        authContext,
      )
      const data = await res.json()
      if (!res.ok) {
        setError(data.error || 'Failed to load')
        return
      }
      setMap(data.severityChannels || {})
    } catch (e) {
      setError(e.message || 'Failed to load')
    } finally {
      setLoading(false)
    }
  }, [orgId, authContext])

  useEffect(() => {
    load()
  }, [load])

  const toggle = (severity, channel) => {
    setMap(prev => {
      const cur = new Set(prev[severity] || [])
      if (cur.has(channel)) cur.delete(channel)
      else cur.add(channel)
      return { ...prev, [severity]: Array.from(cur) }
    })
  }

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      const res = await fetcher(
        `/api/admin/organization/${orgId}/settings/notifications`,
        { method: 'PUT', body: JSON.stringify({ severityChannels: map }) },
        authContext,
      )
      const data = await res.json()
      if (!res.ok) {
        setError(data.error || 'Failed to save')
        return
      }
      if (data.severityChannels) setMap(data.severityChannels)
      setSaved(true)
      setTimeout(() => setSaved(false), 8000)
      toast.success('Notification settings saved')
    } catch (e) {
      setError(e.message || 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="mb-4">
        <h3 className="text-sm font-semibold">Notification Defaults</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Which channels notify the owner for each drift severity. Selecting a
          digest channel batches that severity into an emailed summary instead
          of a real-time alert. R40.
        </p>
      </div>

      {saved && (
        <Alert className="mb-3 border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-200">
          <CheckCircle2 className="size-4" />
          <AlertDescription>Notification settings saved</AlertDescription>
        </Alert>
      )}
      {error && (
        <Alert variant="destructive" className="mb-3">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Severity</TableHead>
                {CHANNELS.map(ch => (
                  <TableHead key={ch.value} className="text-center">
                    {ch.label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {SEVERITIES.map(sev => (
                <TableRow key={sev.value}>
                  <TableCell className="font-medium">{sev.label}</TableCell>
                  {CHANNELS.map(ch => (
                    <TableCell key={ch.value} className="text-center">
                      <Checkbox
                        checked={(map[sev.value] || []).includes(ch.value)}
                        onCheckedChange={() => toggle(sev.value, ch.value)}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="mt-4 flex justify-end">
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="mr-2 size-4 animate-spin" />}
              Save
            </Button>
          </div>
        </>
      )}
    </div>
  )
}
